import type { Coordinates } from '@volkswagen-onehub/audi-feature-service-visitor-locator';
import type { Boundary } from '../utils/geocoderResultProcessor';
import { calculateZoomLevelWithBoundary } from '../utils/geocoderResultProcessor';
import mapContext from '../contexts/map-context';
import { useWindowSize } from './useWindowSize';
import { DEFAULT_CENTER_COORDINATES } from '../components/map/Map/MapUtils';

const LATITUDE_CONVERSION_UNIT = 110.574;
const LONGITUDE_CONVERSION_UNIT = 111.32;

export const useMapProperty = () => {
  const { searchRadius, results: dealers } = mapContext.useMapState();
  const windowSize = useWindowSize();

  const calculateBoundaryWithRadius = (calculatedCenter: Coordinates): Boundary => {
    const swLat = calculatedCenter.lat - searchRadius / LATITUDE_CONVERSION_UNIT;
    const neLat = calculatedCenter.lat + searchRadius / LATITUDE_CONVERSION_UNIT;
    const swLng =
      calculatedCenter.lng -
      searchRadius / LONGITUDE_CONVERSION_UNIT / Math.cos((swLat / 180) * Math.PI);
    const neLng =
      calculatedCenter.lng +
      searchRadius / LONGITUDE_CONVERSION_UNIT / Math.cos((swLat / 180) * Math.PI);
    return {
      ne: {
        lat: neLat,
        lng: neLng,
      },
      sw: {
        lat: swLat,
        lng: swLng,
      },
    };
  };

  const calculateBoundaryWithoutRadius = (): Boundary => {
    let swLat = Number.MAX_SAFE_INTEGER;
    let swLng = Number.MAX_SAFE_INTEGER;
    let neLat = Number.MIN_SAFE_INTEGER;
    let neLng = Number.MIN_SAFE_INTEGER;

    dealers?.forEach((dealer) => {
      swLat = Math.min(swLat, dealer.latitude);
      neLat = Math.max(neLat, dealer.latitude);
      swLng = Math.min(swLng, dealer.longitude);
      neLng = Math.max(neLng, dealer.longitude);
    });
    return {
      ne: {
        lat: neLat === Number.MIN_SAFE_INTEGER ? 0 : neLat,
        lng: neLng === Number.MIN_SAFE_INTEGER ? 0 : neLng,
      },
      sw: {
        lat: swLat === Number.MAX_SAFE_INTEGER ? 0 : swLat,
        lng: swLng === Number.MAX_SAFE_INTEGER ? 0 : swLng,
      },
    };
  };

  const calculateMapCenterForDealers = (): Coordinates => {
    const boundary: Boundary = calculateBoundaryWithoutRadius();
    return {
      lat: (boundary.sw.lat + boundary.ne.lat) / 2,
      lng: (boundary.sw.lng + boundary.ne.lng) / 2,
    };
  };

  const calculateMapZoomLevel = (
    calculatedCenter?: Coordinates,
    updateWithRadius?: boolean,
  ): number => {
    if (updateWithRadius) {
      return calculateZoomLevelWithBoundary(
        calculateBoundaryWithRadius(calculatedCenter || DEFAULT_CENTER_COORDINATES),
        windowSize,
        false,
      );
    }
    return calculateZoomLevelWithBoundary(calculateBoundaryWithoutRadius(), windowSize, false);
  };

  return {
    calculateMapZoomLevel,
    calculateMapCenterForDealers,
  };
};
